import React, { useEffect, useContext, useState } from "react";
import {
  ITravelOfferCardContent,
  TravelWalletDetailsBanner,
  getTravelOfferCardContent,
  getTravelCreditCardContent,
  TravelCreditDetailsCardContent,
  IconName,
  Icon,
  getTravelCreditHistoryModalProps,
  useDeviceTypes,
  isOfferHSPTiered,
  getWalletBannerCreditToShow,
  ITravelWalletDetailsBannerVariant,
  isSpendAndGetOffer,
} from "halifax";

import {
  SelectedTravelOfferProperties,
  SELECTED_TRAVEL_OFFER,
  VIEWED_OFFER_FAQ,
  TravelWalletOffer,
  VIEWED_CREDIT_BANNER,
  ENGAGED_CREDIT_CTA,
  SelectedTravelOfferScreen,
  VIEW_CREDIT_HISTORY,
  ENGAGED_OFFER_CTA,
  VIEWED_OFFER_BANNER,
  CorpSessionInfo,
  RestrictedToProductsEnum,
} from "redmond";
import { RouteComponentProps } from "react-router-dom";
import {
  getTravelOfferBannerText,
  VIEW_OFFER_DETAILS_CTA_COPY,
  VIEW_DETAILS_CTA_COPY,
  getTravelCreditBannerText,
  CREDITS_MODAL_HEADER_TEXT,
} from "./textConstants";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { TravelWalletDetailsBannerConnectorProps } from "./container";
import { ClientContext } from "../../../../App";
import "./styles.scss";
import { HSP_OFFER_TITLE_LABEL } from "../TravelWalletDrawer/constants";
import { config } from "../../../../api/config";
import {
  AVAILABLE,
  CORE_HOMEPAGE,
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";

interface IPackagesTravelWalletDetailsBannerProps
  extends TravelWalletDetailsBannerConnectorProps,
    RouteComponentProps {
  onDismiss?: () => void;
  offer?: TravelWalletOffer;
  bannerTextType: "availability" | "shop";
  showButton?: boolean;
  className?: string;
  screen?: SelectedTravelOfferProperties["screen"];
  showOffer?: boolean;
  variant?: ITravelWalletDetailsBannerVariant;
}

export const PackagesTravelWalletDetailsBanner = (
  props: IPackagesTravelWalletDetailsBannerProps,
) => {
  const {
    offer,
    onDismiss,
    showButton,
    className,
    screen,
    creditBreakdown,
    credit,
    bannerTextType,
    creditsTransactionHistory,
    rewardsAccounts,
    variant,
  } = props;

  const { matchesMobile } = useDeviceTypes();
  const { sessionInfo } = useContext(ClientContext);

  const [openModal, setOpenModal] = useState(false);
  const [bestOffer, setBestOffer] = useState(offer);
  const [
    travelWalletDetailsBannerModalContent,
    setTravelWalletDetailsBannerModalContent,
  ] = useState<ITravelOfferCardContent | TravelCreditDetailsCardContent | null>(
    null,
  );
  const [travelWalletDetailsBannerText, setTravelWalletDetailsBannerText] =
    useState("");
  const [isCredit, setIsCredit] = useState(false);

  const firstName = sessionInfo?.userInfo?.firstName || "";
  const isNonFinancialUser =
    (sessionInfo as CorpSessionInfo)?.corporateInfo?.cap1Role === "NFU";

  const expState = useExperiments();

  const isCoreHomepageEnabled = getExperimentVariant(
    expState.experiments,
    CORE_HOMEPAGE,
  ).startsWith(AVAILABLE);

  const creditDetailToShow = getWalletBannerCreditToShow(
    credit?.breakdown || creditBreakdown,
    rewardsAccounts,
    RestrictedToProductsEnum.Packages,
  );

  useEffect(() => {
    if (offer && bestOffer !== offer) {
      setBestOffer(offer);
    }
    if (credit) {
      // offer as a prop comes from /availability as the best offer so that amount is the same as the credit amount, then we know the best offer we get back is credit (so we display credit text)
      const bestOfferIsACredit = !!(
        !bestOffer ||
        Math.abs(credit.amount.amount) === Math.abs(bestOffer.amount.amount) ||
        !!creditDetailToShow
      );
      setIsCredit(bestOfferIsACredit);
      if (bestOfferIsACredit) {
        trackEvent({
          eventName: VIEWED_CREDIT_BANNER,
          properties: {},
        });
      } else {
        trackEvent({
          eventName: VIEWED_OFFER_BANNER,
          properties: {
            location: screen,
          },
          encryptedProperties: bestOffer
            ? [bestOffer.trackingPropertiesV2?.encryptedProperties ?? ""]
            : [],
        });
      }
    } else {
      setIsCredit(false);
    }
  }, [offer, credit]);

  useEffect(() => {
    const content =
      isCredit && credit
        ? getTravelCreditCardContent(
            creditBreakdown,
            config.TENANT,
            firstName,
            credit.amount,
            true,
            isNonFinancialUser,
          )
        : bestOffer
        ? getTravelOfferCardContent(bestOffer, true)
        : undefined;
    setTravelWalletDetailsBannerModalContent(content);
    const text =
      isCredit && credit
        ? getTravelCreditBannerText(credit, creditDetailToShow)
        : bestOffer
        ? getTravelOfferBannerText(bestOffer, bannerTextType)
        : undefined;
    text && setTravelWalletDetailsBannerText(text);
  }, [isCredit, creditDetailToShow]);

  const isOfferSpendAndGet = offer && isSpendAndGetOffer(offer.descriptions);

  if (
    (!bestOffer && (isCredit ? !credit && !creditDetailToShow : true)) ||
    !travelWalletDetailsBannerText ||
    isOfferSpendAndGet
  )
    return null;

  if (isCredit && isCoreHomepageEnabled) return null;

  return isCredit ? (
    <TravelWalletDetailsBanner
      className={className}
      openModal={openModal}
      onDismissClick={onDismiss}
      setOpenModal={(open) => {
        setOpenModal(open);
        if (open) {
          trackEvent({
            eventName: ENGAGED_CREDIT_CTA,
            properties: {},
          });
        }
      }}
      travelOfferBannerText={travelWalletDetailsBannerText}
      modalHeaderText={CREDITS_MODAL_HEADER_TEXT(firstName)}
      modalCTACopy={VIEW_DETAILS_CTA_COPY}
      showButton={showButton}
      onFirstTravelOfferCardChange={() => {
        trackEvent({
          eventName: VIEWED_OFFER_FAQ,
          properties: {
            screen,
          },
        });
      }}
      creditDetailsCardContent={travelWalletDetailsBannerModalContent}
      modalVariant="credit"
      icon={<Icon name={IconName.PiggyBank} />}
      showCreditHistory={!!creditsTransactionHistory?.length}
      travelCreditHistoryProps={
        creditsTransactionHistory?.length
          ? {
              ...getTravelCreditHistoryModalProps(
                firstName,
                creditsTransactionHistory,
                credit,
                !matchesMobile ? IconName.PiggyBankCircleOutline : undefined,
              ),
              rewardsAccounts,
              credit,
              isAnnualTravelCreditsExperiment: true,
            }
          : undefined
      }
      isAnnualTravelCreditsExperiment
      onViewCreditHistory={() =>
        trackEvent({
          eventName: VIEW_CREDIT_HISTORY,
          properties: {},
        })
      }
      restrictedCreditDetail={creditDetailToShow}
      variant={variant}
    />
  ) : (
    <TravelWalletDetailsBanner
      className={className}
      openModal={openModal}
      onDismissClick={
        screen === SelectedTravelOfferScreen.PACKAGES_HOTEL_SHOP &&
        !matchesMobile
          ? onDismiss
          : undefined
      }
      setOpenModal={(open) => {
        setOpenModal(open);
        if (open && bestOffer) {
          trackEvent({
            eventName: SELECTED_TRAVEL_OFFER,
            properties: {
              screen,
              ...bestOffer.trackingPropertiesV2?.properties,
            },
            encryptedProperties: [
              bestOffer.trackingPropertiesV2?.encryptedProperties ?? "",
            ],
          });

          trackEvent({
            eventName: ENGAGED_OFFER_CTA,
            properties: {
              location: screen,
              entry_type: "banner",
              funnel: bestOffer.funnels.join(","),
            },
            encryptedProperties: [
              bestOffer.trackingPropertiesV2?.encryptedProperties ?? "",
            ],
          });
        }
      }}
      travelOfferCardContent={travelWalletDetailsBannerModalContent}
      travelOfferBannerText={travelWalletDetailsBannerText}
      modalCTACopy={
        screen === SelectedTravelOfferScreen.PACKAGES_HOTEL_SHOP
          ? VIEW_DETAILS_CTA_COPY
          : VIEW_OFFER_DETAILS_CTA_COPY
      }
      showButton={showButton}
      onFirstTravelOfferCardChange={() => {
        trackEvent({
          eventName: VIEWED_OFFER_FAQ,
          properties: {
            screen,
          },
        });
      }}
      offerFunnels={offer?.funnels}
      offerLocation={offer?.descriptions[2]}
      imageId={offer?.imageId}
      imageFolderUrl={offer?.imageFolderUrl}
      modalTitleLabelText={
        offer && isOfferHSPTiered(offer.descriptions)
          ? HSP_OFFER_TITLE_LABEL
          : undefined
      }
      variant={variant}
    />
  );
};
